import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { APIContext } from "../../contexts/APIContext";
import { GraphicsContext } from "../../contexts/GraphicsContext";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { ScrollBar } from "../../Theme/Hyper";
import {
  Panel,
  PanelTitle,
  SubTitle,
  Controls,
  Field,
  Label,
  Button,
  Empty,
} from "./Styles";
import TournamentTreePanel from "./TournamentTreePanel";

// The EFL view takes its height from the 100vh right sidebar, but anything
// taller grows the whole view past the window, where the body clips it rather
// than scrolling. Absolutely positioned, the scroll area adds no height of its
// own: it fills the space left below the team names and scrolls inside it,
// which also gives the panels a real height to take a third of.
const Frame = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

const Main = styled(ScrollBar)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: 1em;
  box-sizing: border-box;
`;

function ScrollArea({ children }) {
  return (
    <Frame>
      <Main>{children}</Main>
    </Frame>
  );
}

// The table and fixture lists can run long, so each panel is capped at a
// third of the scroll area and scrolls its results internally instead.
const ListPanel = styled(Panel)`
  max-height: calc(100% / 3);
`;

const InputField = styled.input`
  display: flex;
  padding: 0.5em;
  font-size: 0.875rem;
  color: #fff;
  background-color: #0e1219;
  border: 1px solid #384454;
  border-radius: 3px;
  box-sizing: border-box;
  width: ${(props) => (props.wide ? "170px" : "90px")};
  color-scheme: dark;
  :focus {
    border-color: #14ec8e;
    outline: none;
  }
`;

const Result = styled(ScrollBar)`
  display: flex;
  flex-direction: column;
  margin-top: 1em;
  font-size: 0.8em;
  min-height: 0;
  overflow-y: auto;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.2em 0;
  border-bottom: 1px solid #202731;
  opacity: ${(props) => (props.header ? "0.5" : "1")};
`;

const Rank = styled.div`
  display: flex;
  min-width: 30px;
  opacity: 0.6;
`;

const TeamName = styled.div`
  display: flex;
  min-width: 180px;
  padding-right: 1em;
  box-sizing: border-box;
`;

const Stat = styled.div`
  display: flex;
  min-width: 45px;
  font-weight: ${(props) => (props.bold ? "500" : "100")};
`;

const FixtureDate = styled.div`
  display: flex;
  min-width: 55px;
  opacity: 0.6;
`;

// The League Cup (Carabao Cup) is the competition EFLRightSideBar drops mini
// standings for.
const LEAGUE_CUP = {
  _id: "65f1b89868caf02ffdc47ff5",
  opta_ID: "725gd73msyt08xm76v7gkxj7u",
};
const WOMENS_LEAGUE_CUP = {
  _id: "68d29dea09c6786c41d37969",
  opta_ID: "5ixq8vcn9w12n74fuj6oj9lj7",
};
const EFL_TROPHY = {
  _id: "65f1b89868caf02ffdc4800a",
  opta_ID: "f47f3717z2vtpxfxrpdd4jl1x",
};
// Cups that get the tournament tree, alongside DFB games.
const TREE_COMPETITIONS = [LEAGUE_CUP, WOMENS_LEAGUE_CUP, EFL_TROPHY];

function isCompetition(game, competition) {
  return (
    (competition._id && game?.competition?._id === competition._id) ||
    (competition.opta_ID && game?.competition?.opta_ID === competition.opta_ID)
  );
}

function isDFB(game) {
  return game?.dfb_ID != null;
}

// The League Cup and DFB games have no league table to customise.
function hasLeagueTable(game) {
  return !isDFB(game) && !isCompetition(game, LEAGUE_CUP);
}

function hasTournamentTree(game) {
  return (
    isDFB(game) || TREE_COMPETITIONS.some((c) => isCompetition(game, c))
  );
}

export default function CustomGraphics({ game }) {
  const { getStandings, getFixturesByDateRange } = useContext(APIContext);
  const { previewGraphic } = useContext(GraphicsContext);

  const [standings, setStandings] = useState();
  const [fixtures, setFixtures] = useState();
  const [fixturesLoaded, setFixturesLoaded] = useState(false);
  // Saved so they survive switching tabs: the table range per season, and the
  // date range per game, since it defaults to that game's week.
  const [startPosition, setStartPosition] = useLocalStorage(
    "hyper_football_custom_table_start_" + game?.season?._id,
    1,
  );
  const [endPosition, setEndPosition] = useLocalStorage(
    "hyper_football_custom_table_end_" + game?.season?._id,
    6,
  );
  const [startDate, setStartDate] = useLocalStorage(
    "hyper_football_custom_fixtures_start_" + game?._id,
    moment(game?.date).startOf("isoWeek").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useLocalStorage(
    "hyper_football_custom_fixtures_end_" + game?._id,
    moment(game?.date).endOf("isoWeek").format("YYYY-MM-DD"),
  );

  let showTable = hasLeagueTable(game);
  let showTree = hasTournamentTree(game);

  useEffect(() => {
    if (!showTable) return;
    getStandings({
      season: game?.season?._id,
      date: game?.date?.replace("Z", ""),
    }).then((data) => {
      setStandings(data);
    });
  }, [game?._id]);

  // The table graphic renders whatever teams it is handed, in order, so a
  // custom range is just a slice of the ranked teams.
  function teamsInRange(table) {
    let teams = [...(table?.teams || [])].sort(
      (a, b) => (a?.rank ?? 0) - (b?.rank ?? 0),
    );
    let from = Math.max(1, Math.min(startPosition, endPosition));
    let to = Math.max(startPosition, endPosition);
    return teams.slice(from - 1, to);
  }

  // A custom range is not "as it stands", so the scene title is overridden.
  async function previewTable(graphic, type) {
    let latest = await getStandings({
      season: game?.season?._id,
      date: game?.date?.replace("Z", ""),
    });
    setStandings(latest);

    previewGraphic(graphic, {
      game,
      standings: { ...latest, teams: teamsInRange(latest) },
      title: "League Table",
      type,
    });
  }

  async function loadFixtures() {
    let results = await getFixturesByDateRange({
      start_date: startDate + "Z",
      end_date: endDate + "Z",
      competition: game?.competition?._id,
    });

    results = (results || [])?.filter(
      (f) => f?.competition?._id === game?.competition?._id,
    );
    results = results.map((fix) => {
      return {
        ...fix,
        home_scorers: new Array(
          (fix.opta_scores?.total?.home ?? 0) -
            (fix.opta_scores?.pen?.home ?? 0),
        ),
        away_scorers: new Array(
          (fix.opta_scores?.total?.away ?? 0) -
            (fix.opta_scores?.pen?.away ?? 0),
        ),
      };
    });

    setFixtures(results);
    setFixturesLoaded(true);
    return results;
  }

  async function previewFixtures() {
    let results = await loadFixtures();
    previewGraphic("Fixtures", {
      fixtures: results,
      game,
    });
  }

  let preview = teamsInRange(standings);

  return (
    <ScrollArea>
      {showTable && (
        <ListPanel>
          <PanelTitle>Custom Table</PanelTitle>
          <SubTitle>
            {game?.competition?.name}
            {game?.season?.name ? " — " + game?.season?.name : ""}
          </SubTitle>
          <Controls>
            <Field>
              <Label>Start Position</Label>
              <InputField
                type="number"
                min="1"
                value={startPosition}
                onChange={(e) => {
                  setStartPosition(parseInt(e.target.value) || 1);
                }}
              />
            </Field>
            <Field>
              <Label>End Position</Label>
              <InputField
                type="number"
                min="1"
                value={endPosition}
                onChange={(e) => {
                  setEndPosition(parseInt(e.target.value) || 1);
                }}
              />
            </Field>
            <Button
              disabled={preview?.length === 0}
              onClick={() => previewTable("Standings")}
            >
              Preview Table
            </Button>
            <Button
              disabled={preview?.length === 0}
              onClick={() => previewTable("MiniStandings", "TOP")}
            >
              Preview Mini Table
            </Button>
          </Controls>
          {preview?.length > 0 && (
            <Result>
              <ResultRow header>
                <Rank>#</Rank>
                <TeamName>Team</TeamName>
                <Stat>P</Stat>
                <Stat>W</Stat>
                <Stat>L</Stat>
                <Stat>GD</Stat>
                <Stat>PTS</Stat>
              </ResultRow>
              {preview.map((team) => {
                return (
                  <ResultRow key={team?.team?.code ?? team?.team?.name}>
                    <Rank>{team?.rank ?? ""}</Rank>
                    <TeamName>{team?.team?.name}</TeamName>
                    <Stat>{team?.matches_played ?? ""}</Stat>
                    <Stat>{team?.matches_won ?? ""}</Stat>
                    <Stat>{team?.matches_lost ?? ""}</Stat>
                    <Stat>{team?.goal_difference ?? ""}</Stat>
                    <Stat bold>{team?.points ?? ""}</Stat>
                  </ResultRow>
                );
              })}
            </Result>
          )}
          {standings && preview?.length === 0 && (
            <Empty>
              No teams in positions {startPosition}-{endPosition}.
            </Empty>
          )}
          {!standings && <Empty>Loading table…</Empty>}
        </ListPanel>
      )}

      <ListPanel>
        <PanelTitle>Custom Fixtures/Results</PanelTitle>
        <SubTitle>{game?.competition?.name}</SubTitle>
        <Controls>
          <Field>
            <Label>Start Date</Label>
            <InputField
              wide
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setFixturesLoaded(false);
              }}
            />
          </Field>
          <Field>
            <Label>End Date</Label>
            <InputField
              wide
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setFixturesLoaded(false);
              }}
            />
          </Field>
          <Button disabled={!startDate || !endDate} onClick={loadFixtures}>
            Load Fixtures
          </Button>
          <Button disabled={!startDate || !endDate} onClick={previewFixtures}>
            Preview Fixtures
          </Button>
        </Controls>
        {fixtures?.length > 0 && (
          <Result>
            {fixtures.map((fixture) => {
              return (
                <ResultRow key={fixture?._id}>
                  <FixtureDate>
                    {moment(fixture?.date).format("DD/MM")}
                  </FixtureDate>
                  {fixture?.home_team?.short_name ?? fixture?.home_team?.name}
                  {" " + (fixture?.home_scorers?.length ?? 0)}
                  {" - "}
                  {(fixture?.away_scorers?.length ?? 0) + " "}
                  {fixture?.away_team?.short_name ?? fixture?.away_team?.name}
                </ResultRow>
              );
            })}
          </Result>
        )}
        {fixturesLoaded && fixtures?.length === 0 && (
          <Empty>No fixtures found in this date range.</Empty>
        )}
      </ListPanel>

      {/* Keyed by season so the saved tree edits reload with the season. */}
      {showTree && <TournamentTreePanel key={game?.season?._id} game={game} />}
    </ScrollArea>
  );
}
